/**
 * Emoji Picker Component
 * 
 * A grid of clickable emojis for selecting workspace icons.
 * Organized by categories with a clean, accessible interface.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

// Emoji categories with commonly used emojis
const EMOJI_CATEGORIES = {
  people: {
    labelKey: 'emojiPicker.categories.people',
    emojis: ['👤', '👥', '👨‍👩‍👧‍👦', '👨‍💼', '👩‍💼', '🧑‍💻', '👷', '👨‍🔧', '👨‍🍳', '👨‍🎨', '👨‍⚕️', '👨‍🏫'],
  },
  business: {
    labelKey: 'emojiPicker.categories.business',
    emojis: ['💼', '🏢', '🏦', '🏭', '🏪', '🏬', '📊', '📈', '💰', '💵', '💳', '🧾'],
  },
  objects: {
    labelKey: 'emojiPicker.categories.objects',
    emojis: ['📁', '📂', '📋', '📝', '✏️', '📎', '📌', '🔑', '🔐', '💡', '⚙️', '🔧'],
  },
  symbols: {
    labelKey: 'emojiPicker.categories.symbols',
    emojis: ['⭐', '🌟', '✨', '💫', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤'],
  },
  nature: {
    labelKey: 'emojiPicker.categories.nature',
    emojis: ['🏠', '🏡', '🌳', '🌴', '🌺', '🌻', '🌈', '☀️', '🌙', '⛰️', '🏔️', '🌊'],
  },
  activities: {
    labelKey: 'emojiPicker.categories.activities',
    emojis: ['🎯', '🎮', '🎨', '🎭', '🎪', '🎸', '📸', '🎬', '🏆', '🥇', '⚽', '🎾'],
  },
  animals: {
    labelKey: 'emojiPicker.categories.animals',
    emojis: ['🐱', '🐶', '🦁', '🐻', '🦊', '🐰', '🦅', '🦋', '🐝', '🐬', '🦄', '🐉'],
  },
  food: {
    labelKey: 'emojiPicker.categories.food',
    emojis: ['☕', '🍵', '🍺', '🍷', '🍕', '🍔', '🎂', '🍎', '🍊', '🥗', '🍜', '🧁'],
  },
};

type EmojiCategory = keyof typeof EMOJI_CATEGORIES;

interface EmojiPickerProps {
  value?: string;
  onChange: (emoji: string) => void;
  className?: string;
}

export const EmojiPicker = ({ value, onChange, className }: EmojiPickerProps) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<EmojiCategory>('business');

  const categories = Object.keys(EMOJI_CATEGORIES) as EmojiCategory[];

  return (
    <div className={cn('bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden', className)}>
      {/* Category tabs */}
      <div className="flex overflow-x-auto border-b border-slate-200 dark:border-slate-700 scrollbar-hide">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            onClick={() => setActiveCategory(category)}
            className={cn(
              'flex-shrink-0 px-3 py-2 text-xs font-medium transition-colors',
              activeCategory === category
                ? 'text-violet-600 dark:text-violet-400 border-b-2 border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            )}
          >
            {t(EMOJI_CATEGORIES[category].labelKey, category)}
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div className="p-3">
        <div className="grid grid-cols-6 gap-1">
          {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => onChange(emoji)}
              className={cn(
                'w-10 h-10 flex items-center justify-center text-2xl rounded-lg transition-all',
                'hover:bg-slate-100 dark:hover:bg-slate-700 hover:scale-110',
                value === emoji && 'bg-violet-100 dark:bg-violet-900/50 ring-2 ring-violet-500'
              )}
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Selected indicator */}
      {value && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span>{t('emojiPicker.selected', 'Sélectionné')}:</span>
            <span className="text-2xl">{value}</span>
            <button
              type="button"
              onClick={() => onChange('')}
              className="ml-auto text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            >
              {t('emojiPicker.clear', 'Effacer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
